import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API_BASE_URL from '../../../../js/urlHelper';
import { fetchWithAuth } from '../../../../js/authToken';
import ClienteSearch from './ClienteSearch';
import LoanDetails from './LoanDetails';
import LoanResults from './LoanResults';
import AdditionalFields from './AdditionalFields';
import ConfirmationModal from './ConfirmationModal';
import AdvisorSearch from './AdvisorSearch';
import PrestamoSearch from './PrestamoSearch';
import { toast } from 'react-toastify';
import LoadingScreen from '../../../Reutilizables/FetchWithGif';
import { v4 as uuidv4 } from 'uuid';
import { calculateLoan } from '../../../../utilities/loanCalculator';
import { LIMITES_PRESTAMO } from './constants';

function getCurrentDate(addDays = 0) {
  const options = { timeZone: 'America/Lima' };
  const today = new Date();
  if (addDays > 0) {
    today.setDate(today.getDate() + addDays);
  }
  const year = today.toLocaleString('en-US', { year: 'numeric', ...options });
  const month = String(today.toLocaleString('en-US', { month: 'numeric', ...options })).padStart(2, '0');
  const day = String(today.toLocaleString('en-US', { day: 'numeric', ...options })).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createDefaultLoanData() {
  return {
    id: uuidv4(),
    cliente: '',
    clienteId: null,
    credito: '',
    frecuencia: '',
    interes: '',
    cuotas: '',
    cuota: '',
    total: '',
    fecha: getCurrentDate(0),
    fechaGeneracion: getCurrentDate(0),
    fechaInicio: getCurrentDate(0),
    hora_cobranza: '',
    modalidad: '',
    diasACorrer: 0,
    advisorData: null,
    idProducto: null,
    producto: null,
    abonado_por: 'CUENTA CORRIENTE',
    idPrestamo: null,
  };
}

export default function Calculadora({ allowEditing = true, apiBasePath = '/api/admin', allowGroupLoan = true }) {
  const [isGroupLoan, setIsGroupLoan] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [advisor, setAdvisor] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPrestamoSearch, setShowPrestamoSearch] = useState(false);
  const [limitesExtendidos, setLimitesExtendidos] = useState(false);

  const [formDataList, setFormDataList] = useState(() => [createDefaultLoanData()]);

  const indexedErrors = useMemo(() => {
    const map = {};
    Object.entries(errors).forEach(([key, value]) => {
      const parts = key.split('_');
      const last = parts[parts.length - 1];
      const index = Number(last);
      if (!Number.isNaN(index)) {
        const field = parts.slice(0, -1).join('_');
        if (!map[index]) map[index] = {};
        map[index][field] = value;
      }
    });
    return map;
  }, [errors]);

  const updateDates = useCallback(() => {
    setFormDataList(prev =>
      prev.map(formData => {
        const currentDate = getCurrentDate(0);
        const diasACorrer = parseInt(formData.diasACorrer) || 0;
        const startDate = diasACorrer > 0 ? getCurrentDate(diasACorrer) : currentDate;
        return {
          ...formData,
          fecha: startDate,
          fechaGeneracion: currentDate,
          fechaInicio: startDate,
        };
      })
    );
  }, []);

  const diasACorrerValues = useMemo(() => 
    formDataList.map(data => data.diasACorrer).join('-'),
    [formDataList]
  );

  useEffect(() => {
    updateDates();
  }, [updateDates, diasACorrerValues]);

  useEffect(() => {
    if (!allowGroupLoan) {
      setIsGroupLoan(false);
      setGroupName('');
      setAdvisor(null);
    }
  }, [allowGroupLoan]);

  const handleSelectPrestamo = (prestamo) => {
    if (!allowEditing) {
      return;
    }
    const currentDate = getCurrentDate(0);
    if (prestamo.fecha_generacion !== currentDate) {
      toast.error('Solo se pueden editar préstamos creados hoy.');
      return;
    }

    const newFormData = {
      ...createDefaultLoanData(),
      cliente: `${prestamo.cliente.datos.nombre} ${prestamo.cliente.datos.apellidoPaterno} ${prestamo.cliente.datos.apellidoMaterno} ${prestamo.cliente.datos.apellidoConyuge || ''}`.trim(),
      clienteId: prestamo.idCliente,
      credito: prestamo.monto.toString(),
      frecuencia: prestamo.frecuencia,
      interes: prestamo.interes.toString(),
      cuotas: prestamo.cuotas.toString(),
      cuota: prestamo.valor_cuota.toString(),
      total: prestamo.total.toString(),
      fecha: prestamo.fecha_inicio,
      fechaGeneracion: prestamo.fecha_generacion,
      fechaInicio: prestamo.fecha_inicio,
      hora_cobranza: prestamo.hora_cobranza || '',
      modalidad: prestamo.modalidad,
      diasACorrer: (() => {
        if (prestamo.fecha_inicio === prestamo.fecha_generacion) return 0;
        const inicio = new Date(prestamo.fecha_inicio);
        const generacion = new Date(prestamo.fecha_generacion);
        const diff = Math.round((inicio - generacion) / (1000 * 60 * 60 * 24));
        return Math.max(0, Math.min(7, diff));
      })(),
      advisorData: prestamo.asesor,
      idProducto: prestamo.idProducto,
      producto: prestamo.producto || null,
      abonado_por: prestamo.abonado_por || 'CUENTA CORRIENTE',
      idPrestamo: prestamo.idPrestamo,
    };

    setIsEditing(true);
    setFormDataList([newFormData]);
    setIsGroupLoan(!!prestamo.idGrupo);
    if (prestamo.idGrupo) {
      setGroupName('Grupo ' + prestamo.idGrupo);
      setAdvisor(prestamo.asesor);
    }
    setShowPrestamoSearch(false);
  };

  const handleInputChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    if (name === 'limitesExtendidos') {
      handleLimitesExtendidosChange(fieldValue);
      return;
    }

    setFormDataList(prev => {
      const newList = [...prev];
      const updatedFormData = { ...newList[index], [name]: fieldValue };

      if (['credito', 'interes', 'cuotas'].includes(name)) {
        const { total, cuota } = calculateLoan(
          name === 'credito' ? value : updatedFormData.credito,
          name === 'interes' ? value : updatedFormData.interes,
          name === 'cuotas' ? value : updatedFormData.cuotas
        );
        updatedFormData.total = total;
        updatedFormData.cuota = cuota;
      }

      newList[index] = updatedFormData;
      return newList;
    });
  };

  const getLimitesActuales = () => {
    return limitesExtendidos 
      ? LIMITES_PRESTAMO.EXTENDIDO 
      : LIMITES_PRESTAMO.ESTANDAR;
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['credito', 'frecuencia', 'interes', 'cuotas', 'fecha', 'modalidad', 'idProducto'];
    const errorMessages = [];
    const limites = getLimitesActuales();

    formDataList.forEach((formData, index) => {
      requiredFields.forEach(field => {
        if (!formData[field]) {
          newErrors[`${field}_${index}`] = 'Este campo es obligatorio';
          errorMessages.push(`${getFieldLabel(field)} (Cliente ${index + 1}): Este campo es obligatorio`);
        }
      });

      if (!formData.clienteId) {
        newErrors[`cliente_${index}`] = 'Debe seleccionar un cliente válido';
        errorMessages.push(`Cliente (Cliente ${index + 1}): Debe seleccionar un cliente válido`);
      }

      const permiteDecenal = Array.isArray(formData.producto?.frecuencias_permitidas)
        ? formData.producto.frecuencias_permitidas.includes('decenal')
        : formData.producto?.tipo_producto === 'CAMPANA' && formData.producto?.activo !== false;

      if (permiteDecenal && formData.frecuencia !== 'decenal') {
        newErrors[`frecuencia_${index}`] = 'Los productos de campaña solo permiten frecuencia decenal';
        errorMessages.push(`Frecuencia (Cliente ${index + 1}): Los productos de campaña solo permiten frecuencia decenal`);
      }

      if (formData.frecuencia === 'decenal' && !permiteDecenal) {
        newErrors[`frecuencia_${index}`] = 'La frecuencia decenal solo aplica para productos de campaña activos';
        errorMessages.push(`Frecuencia (Cliente ${index + 1}): La frecuencia decenal solo aplica para productos de campaña activos`);
      }

      if (formData.credito === '' || isNaN(formData.credito) || Number(formData.credito) <= 0) {
        newErrors[`credito_${index}`] = 'Solo números positivos mayores a 0';
        errorMessages.push(`Crédito (Cliente ${index + 1}): Solo números positivos mayores a 0`);
      }
      if (formData.interes === '' || isNaN(formData.interes) || Number(formData.interes) < limites.tasaInteres.min || Number(formData.interes) > limites.tasaInteres.max) {
        newErrors[`interes_${index}`] = `Debe ser un número entre ${limites.tasaInteres.min} y ${limites.tasaInteres.max} (${limites.label})`;
        errorMessages.push(`Interés (Cliente ${index + 1}): Debe ser un número entre ${limites.tasaInteres.min} y ${limites.tasaInteres.max} (${limites.label})`);
      }
      if (formData.cuotas === '' || isNaN(formData.cuotas) || !Number.isInteger(Number(formData.cuotas)) || Number(formData.cuotas) < limites.numeroCuotas.min || Number(formData.cuotas) > limites.numeroCuotas.max) {
        newErrors[`cuotas_${index}`] = `Debe ser un número entero entre ${limites.numeroCuotas.min} y ${limites.numeroCuotas.max} (${limites.label})`;
        errorMessages.push(`Cuotas (Cliente ${index + 1}): Debe ser un número entero entre ${limites.numeroCuotas.min} y ${limites.numeroCuotas.max} (${limites.label})`);
      }

      if (formData.hora_cobranza && !/^([01]\d|2[0-3]):[0-5]\d$/.test(formData.hora_cobranza)) {
        newErrors[`hora_cobranza_${index}`] = 'Debe tener el formato HH:mm';
        errorMessages.push(`Hora de cobranza (Cliente ${index + 1}): Debe tener el formato HH:mm`);
      }

      if (!isGroupLoan && !formData.advisorData?.idUsuario) {
        newErrors[`asesor_${index}`] = 'Debe seleccionar un asesor válido';
        errorMessages.push(`Asesor (Cliente ${index + 1}): Debe seleccionar un asesor válido`);
      }
    });

    if (isGroupLoan) {
      if (!groupName.trim()) {
        newErrors.groupName = 'El nombre del grupo es obligatorio';
        errorMessages.push('Nombre del grupo: El nombre del grupo es obligatorio');
      }
      if (!advisor?.idUsuario) {
        newErrors.asesor = 'Debe seleccionar un asesor';
        errorMessages.push('Asesor del grupo: Debe seleccionar un asesor');
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(
        <div>
          <p>Por favor corrige los siguientes errores:</p>
          <ul className="list-disc pl-5">
            {errorMessages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>,
        { autoClose: 5000 }
      );
    }
    return Object.keys(newErrors).length === 0;
  };

  const getFieldLabel = (field) => {
    const labels = {
      credito: 'Crédito',
      frecuencia: 'Frecuencia',
      interes: 'Interés',
      cuotas: 'Cuotas',
      fecha: 'Fecha',
      modalidad: 'Modalidad',
      cliente: 'Cliente',
      asesor: 'Asesor',
      idProducto: 'Producto',
      abonado_por: 'Abonado Por',
      hora_cobranza: 'Hora de cobranza',
    };
    return labels[field] || field;
  };

  const handleConfirm = (action) => {
    if (action === 'guardar') {
      const isValid = validateForm();
      if (!isValid) return;
    }
    setConfirmAction(action);
  };

  const handleConfirmYes = async () => {
    if (confirmAction === 'guardar') {
      setLoading(true);
      try {
        let groupId = null;
        if (isGroupLoan) {
          const groupResponse = await fetchWithAuth(`${API_BASE_URL}${apiBasePath}/grupos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nombre: groupName,
              idAsesor: advisor.idUsuario,
              fecha_creacion: getCurrentDate(0),
            }),
          });
          const groupData = await groupResponse.json();
          if (!groupResponse.ok) {
            throw new Error(groupData.error || 'Error al crear grupo');
          }
          groupId = groupData.idGrupo;
        }

        const prestamosData = formDataList.map(formData => ({
          idCliente: formData.clienteId,
          idGrupo: groupId,
          credito: parseFloat(formData.credito),
          frecuencia: formData.frecuencia,
          interes: parseFloat(formData.interes),
          cuotas: parseInt(formData.cuotas, 10),
          cuota: parseFloat(formData.cuota),
          total: parseFloat(formData.total),
          modalidad: formData.modalidad,
          fecha: formData.fecha,
          fechaInicio: formData.fechaInicio,
          fechaGeneracion: formData.fechaGeneracion,
          hora_cobranza: formData.hora_cobranza || null,
          idAsesor: advisor ? advisor.idUsuario : formData.advisorData?.idUsuario,
          idProducto: formData.idProducto,
          abonado_por: formData.abonado_por,
          idPrestamo: formData.idPrestamo, // Include idPrestamo for updates
        }));

        const endpoint = isEditing
          ? `${API_BASE_URL}${apiBasePath}/prestamos/${prestamosData[0].idPrestamo}`
          : isGroupLoan
          ? `${API_BASE_URL}${apiBasePath}/prestamos/grupo`
          : `${API_BASE_URL}${apiBasePath}/prestamo`;
        const method = isEditing ? 'PUT' : 'POST';

        const response = await fetchWithAuth(endpoint, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(isEditing ? prestamosData[0] : isGroupLoan ? { prestamos: prestamosData } : prestamosData[0]),
        });

        const responseData = await response.json();
        if (!response.ok) {
          const newErrors = {};
          if (responseData.errors) {
            prestamosData.forEach((_, index) => {
              Object.keys(responseData.errors).forEach(key => {
                const groupFieldMatch = key.match(/^prestamos\.(\d+)\.(.+)$/);
                const targetIndex = groupFieldMatch ? Number(groupFieldMatch[1]) : index;
                const fieldKey = groupFieldMatch ? groupFieldMatch[2] : key;

                if (targetIndex !== index) return;

                const errorMsg = responseData.errors[key][0];
                const fieldMap = {
                  idCliente: `cliente_${index}`,
                  credito: `credito_${index}`,
                  frecuencia: `frecuencia_${index}`,
                  interes: `interes_${index}`,
                  cuotas: `cuotas_${index}`,
                  cuota: `cuota_${index}`,
                  total: `total_${index}`,
                  modalidad: `modalidad_${index}`,
                  fecha: `fecha_${index}`,
                  fechaInicio: `fechaInicio_${index}`,
                  fechaGeneracion: `fechaGeneracion_${index}`,
                  hora_cobranza: `hora_cobranza_${index}`,
                  idAsesor: isGroupLoan ? 'asesor' : `asesor_${index}`,
                  idProducto: `idProducto_${index}`,
                  abonado_por: `abonado_por_${index}`,
                };
                if (fieldMap[fieldKey]) {
                  newErrors[fieldMap[fieldKey]] = errorMsg;
                }
              });
            });
          }

          if (responseData.error && responseData.error.includes('El cliente')) {
            prestamosData.forEach((_, index) => {
              newErrors[`cliente_${index}`] = responseData.error;
            });
          } else if (responseData.error) {
            toast.error(responseData.error);
          }

          setErrors(newErrors);
          if (Object.keys(newErrors).length > 0) {
            toast.error(
              <div>
                <p>Por favor corrige los siguientes errores del servidor:</p>
                <ul className="list-disc pl-5">
                  {Object.entries(newErrors).map(([key, msg], idx) => (
                    <li key={idx}>
                      {getFieldLabel(key.split('_')[0])} {key.includes('_') ? `(Cliente ${parseInt(key.split('_')[1]) + 1})` : ''}: {msg}
                    </li>
                  ))}
                </ul>
              </div>,
              { autoClose: 5000 }
            );
          }
          throw new Error(responseData.error || 'Error al guardar préstamos');
        }

        if (responseData.success) {
          toast.success(isEditing ? 'Préstamo actualizado correctamente' : 'Préstamos registrados correctamente');
          handleReset();
        } else {
          toast.error('Error desconocido al procesar la solicitud');
        }
      } catch (error) {
        if (!errors || Object.keys(errors).length === 0) {
          toast.error('Error al guardar: ' + error.message);
        }
      } finally {
        setLoading(false);
      }
    } else if (confirmAction === 'cancelar') {
      handleReset();
    }
    setConfirmAction(null);
  };

  const handleLimitesExtendidosChange = (isExtended) => {
    setLimitesExtendidos(isExtended);
    
    const nuevosLimites = isExtended 
      ? LIMITES_PRESTAMO.EXTENDIDO 
      : LIMITES_PRESTAMO.ESTANDAR;
    
    // Ajustar valores si exceden los nuevos límites
    setFormDataList(prev => prev.map(formData => {
      const valoresAjustados = { ...formData };
      
      if (formData.interes && Number(formData.interes) > nuevosLimites.tasaInteres.max) {
        valoresAjustados.interes = nuevosLimites.tasaInteres.max.toString();
      }
      
      if (formData.cuotas && Number(formData.cuotas) > nuevosLimites.numeroCuotas.max) {
        valoresAjustados.cuotas = nuevosLimites.numeroCuotas.max.toString();
      }
      
      return valoresAjustados;
    }));
  };

  const handleReset = () => {
    setGroupName('');
    setIsGroupLoan(false);
    setAdvisor(null);
    setFormDataList([createDefaultLoanData()]);
    setErrors({});
    setIsEditing(false);
    setShowPrestamoSearch(false);
    setLimitesExtendidos(false);
  };

  const handleConfirmNo = () => {
    setConfirmAction(null);
  };

  const addClient = () => {
    setFormDataList(prev => [...prev, createDefaultLoanData()]);
  };

  const removeClient = (index) => {
    setFormDataList(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6 pb-10">
      {loading && <LoadingScreen />}
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Gestión de créditos</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">
            {isEditing ? 'Editar crédito' : 'Registro de créditos'}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Calcula cuotas, configura asesor y registra préstamos individuales o grupales.
          </p>
        </div>
      </section>

      <section className="ui-card p-4 sm:p-6 md:p-8">

        {allowEditing && (
          <div className="mt-6 mb-4">
            <button
              onClick={() => setShowPrestamoSearch(!showPrestamoSearch)}
              className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
            >
            {showPrestamoSearch ? 'OCULTAR BUSCAR PRÉSTAMO' : 'EDITAR PRÉSTAMO'}
            </button>
          </div>
        )}

        {allowEditing && showPrestamoSearch && (
          <PrestamoSearch
            apiBasePath={apiBasePath}
            onSelectPrestamo={handleSelectPrestamo}
            onClear={handleReset}
          />
        )}

        {!showPrestamoSearch && (
          <>
            {allowGroupLoan && (
              <div className="mb-4 md:mb-6">
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <input
                  type="checkbox"
                  id="isGroupLoan"
                  checked={isGroupLoan}
                  onChange={(e) => {
                    setIsGroupLoan(e.target.checked);
                    if (!e.target.checked) {
                      setFormDataList([createDefaultLoanData()]);
                      setGroupName('');
                      setAdvisor(null);
                    }
                  }}
                  className="h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-2 focus:ring-red-500"
                  disabled={isEditing}
                />
                <span className="ml-3 text-sm sm:text-base font-semibold text-slate-700">
                  CRÉDITO GRUPAL
                </span>
                {isGroupLoan && (
                  <>
                    <input
                      type="text"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      placeholder="Nombre del grupo"
                      className={`w-full max-w-xs rounded-xl border px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.groupName ? 'border-red-500' : 'border-slate-300'}`}
                      aria-label="Nombre del grupo"
                      disabled={isEditing}
                    />
                    {errors.groupName && (
                      <p className="text-red-500 text-xs mt-1">{errors.groupName}</p>
                    )}
                  </>
                )}
                </div>
              </div>
            )}

            {isGroupLoan && (
              <div className="mb-4">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                  ASESOR DEL GRUPO:
                </label>
                <AdvisorSearch
                  selectedAdvisor={advisor}
                  onSelect={setAdvisor}
                  onRemove={() => setAdvisor(null)}
                  errors={errors.asesor}
                  disabled={isEditing}
                />
                {errors.asesor && (
                  <p className="text-red-500 text-xs mt-1">{errors.asesor}</p>
                )}
              </div>
            )}

            {formDataList.map((formData, index) => {
              const errorsForIndex = indexedErrors[index] || {};
              return (
              <div key={formData.id} className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Cliente {index + 1}</h3>
                    <p className="text-xs text-slate-500">Complete los datos del préstamo</p>
                  </div>
                  {isGroupLoan && formDataList.length > 1 && !isEditing && (
                    <button
                      onClick={() => removeClient(index)}
                      className="text-sm font-semibold text-red-600 transition hover:text-red-800"
                    >
                      ELIMINAR
                    </button>
                  )}
                </div>
                <div className="mb-4">
                  <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <span className="text-sm sm:text-base font-semibold text-slate-700">
                      CORRER DÍA:
                    </span>
                    <select
                      id={`diasACorrer_${index}`}
                      name="diasACorrer"
                      value={formData.diasACorrer}
                      onChange={(e) => handleInputChange(index, e)}
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                    >
                      <option value="0">Hoy (0 días)</option>
                      <option value="1">1 día</option>
                      <option value="2">2 días</option>
                      <option value="3">3 días</option>
                      <option value="4">4 días</option>
                      <option value="5">5 días</option>
                      <option value="6">6 días</option>
                      <option value="7">7 días</option>
                    </select>
                    <span className="text-sm text-slate-600">
                      Fecha de inicio: {formData.fecha}
                    </span>
                  </div>
                </div>
                <ClienteSearch
                  apiBasePath={apiBasePath}
                  cliente={formData.cliente}
                  setCliente={(value) => setFormDataList(prev => {
                    const newList = [...prev];
                    newList[index] = { ...newList[index], cliente: value };
                    return newList;
                  })}
                  setClienteId={(value) => setFormDataList(prev => {
                    const newList = [...prev];
                    newList[index] = { ...newList[index], clienteId: value };
                    return newList;
                  })}
                  error={errorsForIndex.cliente}
                  setErrors={setErrors}
                  onClear={() => setFormDataList(prev => {
                    const newList = [...prev];
                    newList[index] = { ...newList[index], cliente: '', clienteId: null };
                    return newList;
                  })}
                  disabled={isEditing}
                />
                <LoanDetails
                  apiBasePath={apiBasePath}
                  formData={formData}
                  handleInputChange={(e) => handleInputChange(index, e)}
                  errors={errorsForIndex}
                  limitesExtendidos={limitesExtendidos}
                />
                <LoanResults formData={formData} />
                <AdditionalFields
                  apiBasePath={apiBasePath}
                  formData={formData}
                  handleInputChange={(e) => handleInputChange(index, e)}
                  errors={errorsForIndex}
                  isGroupLoan={isGroupLoan}
                  advisor={advisor}
                  productoSeleccionado={formData.producto}
                />
              </div>
              );
            })}

            {isGroupLoan && !isEditing && (
              <button
                onClick={addClient}
                className="mb-4 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                AGREGAR CLIENTE
              </button>
            )}

            <div className="mt-3 md:mt-6 grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
              <button
                onClick={() => handleConfirm('cancelar')}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                CANCELAR
              </button>
              <button
                onClick={() => handleConfirm('guardar')}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                {isEditing ? 'ACTUALIZAR' : 'GUARDAR'}
              </button>
            </div>
          </>
        )}
      </section>

      {confirmAction && (
        <ConfirmationModal
          action={confirmAction}
          onConfirm={handleConfirmYes}
          onCancel={handleConfirmNo}
        />
      )}
    </div>
  );
}